import { useEffect, useRef, useState } from "react";

/**
 * Persists a form state to localStorage so the user never loses data
 * in case of a network error, browser crash, or accidental tab close.
 *
 * - Auto-saves after `delayMs` of inactivity (default 1500ms).
 * - On mount, exposes the latest stored draft (if any) so the caller
 *   can prompt the user to restore it.
 * - Call `clearDraft()` after a successful server-side save.
 */
export function useDraftPersistence<T>(
  key: string | null | undefined,
  value: T,
  options: { delayMs?: number; enabled?: boolean } = {}
) {
  const { delayMs = 1500, enabled = true } = options;
  const [storedDraft, setStoredDraft] = useState<{ data: T; savedAt: string } | null>(null);
  const timerRef = useRef<number | null>(null);
  const lastKeyRef = useRef<string | null>(null);

  // Load existing draft when key changes
  useEffect(() => {
    if (!key || !enabled) {
      setStoredDraft(null);
      lastKeyRef.current = null;
      return;
    }
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;
    try {
      const raw = localStorage.getItem(`draft:${key}`);
      if (raw) setStoredDraft(JSON.parse(raw));
      else setStoredDraft(null);
    } catch {
      setStoredDraft(null);
    }
  }, [key, enabled]);

  // Debounced auto-save
  useEffect(() => {
    if (!key || !enabled) return;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      try {
        localStorage.setItem(
          `draft:${key}`,
          JSON.stringify({ data: value, savedAt: new Date().toISOString() })
        );
      } catch {
        /* localStorage full / disabled — silently ignore */
      }
    }, delayMs);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [key, value, delayMs, enabled]);

  const clearDraft = () => {
    if (!key) return;
    try {
      localStorage.removeItem(`draft:${key}`);
    } catch {
      /* ignore */
    }
    setStoredDraft(null);
  };

  return { storedDraft, clearDraft };
}
